import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

interface CategoryChartProps {
  startDate: string;
  endDate: string;
}

interface Transaction {
  payment_method: string;
  amount: number;
}

export function CategoryChart({ startDate, endDate }: CategoryChartProps) {
  const { data: transactions, isLoading } = useQuery({
    queryKey: ["transactions-by-payment-method", startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from("transactions")
        .select("payment_method, amount")
        .eq("type", "entrada");

      if (startDate) {
        query = query.gte("created_at", startDate);
      }
      if (endDate) {
        query = query.lte("created_at", endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Transaction[];
    },
  });

  if (isLoading || !transactions) {
    return <div className="flex items-center justify-center h-full">Carregando...</div>;
  }

  const paymentMethodData = transactions.reduce((acc: any[], transaction) => {
    const existingMethod = acc.find(
      (item) => item.name === transaction.payment_method
    );

    if (existingMethod) {
      existingMethod.value += Number(transaction.amount);
    } else {
      acc.push({
        name: transaction.payment_method,
        value: Number(transaction.amount),
      });
    }

    return acc;
  }, []);

  const total = paymentMethodData.reduce((sum, item) => sum + item.value, 0);
  paymentMethodData.forEach((item) => {
    item.percentage = ((item.value / total) * 100).toFixed(1);
  });

  const COLORS = {
    pix: "#0073B7",
    dinheiro: "#004080",
    cartao: "#00A6FF",
    transferencia: "#0059B3",
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1A1F2C] p-3 rounded border border-[#404040]">
          <p className="text-white font-semibold">{`${formatPaymentMethod(payload[0].name)}`}</p>
          <p className="text-white">{`${payload[0].payload.percentage}%`}</p>
          <p className="text-white">
            {new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            }).format(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const formatPaymentMethod = (method: string) => {
    const formats: { [key: string]: string } = {
      pix: "PIX",
      dinheiro: "Dinheiro",
      cartao: "Cartão",
      transferencia: "Transferência",
    };
    return formats[method] || method;
  };

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={paymentMethodData}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {paymentMethodData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.name as keyof typeof COLORS] || "#0073B7"}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={(value) => formatPaymentMethod(value)}
            className="text-white"
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}